import { useCallback, useEffect, useState } from "react";
import { getSystemSettings } from "../../services";
import { SystemSetting } from "../../types/system-setting.type";

interface SystemSettingsState {
  settings: SystemSetting[];
  loading: boolean;
  hasError: boolean;
  reload: () => void;
}

/**
 * The effective system settings (ตั้งค่าระบบ). Read on every mount rather than cached, so a
 * screen always works with what MASTER last saved. A failed load has already been shown by the
 * axios interceptor; `hasError` lets the screen say what it cannot show.
 */
export function useSystemSettings(): SystemSettingsState {
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [reloadCount, setReloadCount] = useState<number>(0);

  useEffect(() => {
    let isLatest = true;
    setLoading(true);
    setHasError(false);
    getSystemSettings()
      .then((response) => {
        if (isLatest) setSettings(response.data.data?.rows ?? []);
      })
      .catch(() => {
        if (isLatest) setHasError(true);
      })
      .finally(() => {
        if (isLatest) setLoading(false);
      });
    return () => {
      isLatest = false;
    };
  }, [reloadCount]);

  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  return { settings, loading, hasError, reload };
}

const findSettingValue = (settings: SystemSetting[], key: string) =>
  settings.find((setting) => setting.key === key)?.value;

/** A number setting's value; undefined while the settings load or when the key is absent. */
export const findNumberSetting = (
  settings: SystemSetting[],
  key: string,
): number | undefined => {
  const value = findSettingValue(settings, key);
  return typeof value === "number" ? value : undefined;
};

/** A code setting's value; undefined while the settings load or when the key is absent. */
export const findTextSetting = (
  settings: SystemSetting[],
  key: string,
): string | undefined => {
  const value = findSettingValue(settings, key);
  return typeof value === "string" ? value : undefined;
};
